import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  selectTouchPoint,
  unSelectTouchPoint,
} from "../../actions/campaign-planning/CampaignPlanningActions";

// Constants And Utils
import { DataLoading, HeaderWithActionButton } from "./PlanningUtils";
import { OrgCityConfigTypes } from "../../../constants/GeneralConstants";

// Components
import CheckBox from "../../../components/check-box/CheckBox";

// get Touch Points Selected Count
function getTouchPointsSelectedCount(points = [], selectedTouchPoints = []) {
  if (selectedTouchPoints.length === 0) {
    return 0;
  }

  const touchPointsSelected = selectedTouchPoints.filter((tp) =>
    points.includes(tp)
  );
  return touchPointsSelected.length;
}

function TouchPointsGroupSection({
  selectedTpGroup,
  setSelectedTpGroup = () => {},
  selectedTouchPoints = [],
  touchPointsGroupsMap = {},
}) {
  const touchPointsGroups = Object.keys(touchPointsGroupsMap) || [];

  useEffect(() => {
    const firstTouchPointGroup = touchPointsGroups[0] || "";
    setSelectedTpGroup(firstTouchPointGroup);
  }, []);

  return (
    <div className="col-6 px-0 border-right">
      <div className="px-3">
        {Object.keys(touchPointsGroupsMap).map((tpGroupKey, index) => {
          const { label, points = [] } = touchPointsGroupsMap[tpGroupKey] || {};
          const isTpGroupSelected = selectedTpGroup === tpGroupKey;
          const selectedBgStyle = isTpGroupSelected ? "bg-alt rounded-lg" : "";
          const touchPointsSelectedCount = getTouchPointsSelectedCount(
            points,
            selectedTouchPoints
          );
          const iconClass = isTpGroupSelected
            ? "fa-angle-down text-primary"
            : "fa-angle-right";
          return (
            <div
              key={index}
              className={`d-flex align-items-center justify-content-between p-2 cursor-pointer my-2 ${selectedBgStyle}`}
              onClick={() => setSelectedTpGroup(tpGroupKey)}
            >
              <p className="mb-0">{label}</p>
              <p className="mb-0">
                {touchPointsSelectedCount > 0 && (
                  <span className="mr-2">({touchPointsSelectedCount})</span>
                )}
                <i className={`fa ${iconClass}`} />
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// hook to get filtered TP based on selected zones
function useGetZonesFilteredTouchPoints(initialTouchPoints = []) {
  // stretch details map (overall stretch data including all medias)
  const roadStretchDetailsMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // applied filters
  const appliedFiltersDetailsMap = useSelector(
    (state) => state.planningRoadStretches.appliedFiltersDetailsMap
  );
  const { zones: appliedZoneIds = [] } = appliedFiltersDetailsMap || {};
  const isZonesFilterApplied = appliedZoneIds.length > 0;

  // if no zones filter applied, return all touch points (initial)
  if (!isZonesFilterApplied) {
    return initialTouchPoints;
  }

  // filter stretches based on zone applied
  const zonesFilteredStretches = Object.values(roadStretchDetailsMap).filter(
    (stretchInfo) => {
      const { zoneId = "" } = stretchInfo || {};
      return appliedZoneIds.includes(zoneId);
    }
  );

  // filtered touch points
  const zonesFilteredTouchPoints = zonesFilteredStretches.flatMap(
    (stretchInfo) => {
      const { metaData = {} } = stretchInfo || {};
      const touchPoints = metaData?.touchPoints || [];
      return touchPoints;
    }
  );

  // only keep those points which are included in zonesFilteredTouchPoints
  const filteredTPFromZonesFiltered = initialTouchPoints.filter((tp) =>
    zonesFilteredTouchPoints?.includes(tp)
  );

  return filteredTPFromZonesFiltered;
}

function TouchPointsCheckboxSection({
  selectedTpGroup,
  selectedTouchPoints = [],
  touchPointsGroupsMap = {},
}) {
  const dispatch = useDispatch();

  // selected TP info
  const selectedTouchPointsGroupInfo =
    touchPointsGroupsMap[selectedTpGroup] || {};
  const { label, types = [] } = selectedTouchPointsGroupInfo || {};

  // get zones filtered touchPoints
  const finalTouchPoints = useGetZonesFilteredTouchPoints(types);

  // function
  function onCheckboxChange(touchPoint, isChecked) {
    if (!isChecked) {
      // select
      dispatch(selectTouchPoint(touchPoint));
    } else {
      dispatch(unSelectTouchPoint(touchPoint));
    }
  }

  return (
    <div className="col-6 px-0 border-left">
      <h5 className="my-1 px-3 py-2">{label}</h5>
      <hr className="my-0" />

      {/* no data */}
      {finalTouchPoints.length === 0 && (
        <p className="text-center font-italic text-muted my-2 px-3 py-2">
          No Touch Points Available
        </p>
      )}

      {/* touch points options */}
      {finalTouchPoints.map((touchPoint, index) => {
        const isChecked = selectedTouchPoints.includes(touchPoint) || false;
        return (
          <div
            key={index}
            className="d-flex justify-content-between mt-1 px-3 py-1"
          >
            <p className="mb-0" title={touchPoint}>
              {touchPoint}
            </p>
            <CheckBox
              boxStyle="mb-0"
              checkBoxKey={touchPoint}
              showLabel={false}
              checked={isChecked}
              onChange={() => onCheckboxChange(touchPoint, isChecked)}
            />
          </div>
        );
      })}
    </div>
  );
}

// Page Components
function TouchPointsCheckboxList({ selectedTouchPoints = [] }) {
  const [selectedTpGroup, setSelectedTpGroup] = useState("");

  // city config
  const cityConfig = useSelector((state) => state.orgCityConfig.cityConfig);
  const cityConfigLoading = useSelector(
    (state) => state.orgCityConfig.cityConfigLoading
  );

  // TouchPoints map
  const touchPointsGroupsMap = cityConfig[OrgCityConfigTypes.touchPoint] || {};

  // loading
  if (cityConfigLoading) {
    return <DataLoading dataType="Touch Points" />;
  }

  return (
    <div className="w-100 d-flex border rounded-lg overflow-hidden">
      {/* left section */}
      <TouchPointsGroupSection
        selectedTpGroup={selectedTpGroup}
        setSelectedTpGroup={setSelectedTpGroup}
        selectedTouchPoints={selectedTouchPoints}
        touchPointsGroupsMap={touchPointsGroupsMap}
      />

      {/* Touch Points Checkbox Section */}
      {selectedTpGroup && (
        <TouchPointsCheckboxSection
          selectedTpGroup={selectedTpGroup}
          selectedTouchPoints={selectedTouchPoints}
          touchPointsGroupsMap={touchPointsGroupsMap}
        />
      )}
    </div>
  );
}

/**
 * Touch Points Tab
 */
export default function TouchPointsTab({
  handleTabHeaderBtnClick = () => {},
  selectedTouchPoints = [],
}) {
  return (
    <>
      <HeaderWithActionButton
        title="Touch Point"
        buttonText="Confirm"
        onButtonClick={handleTabHeaderBtnClick}
      />
      <TouchPointsCheckboxList selectedTouchPoints={selectedTouchPoints} />
    </>
  );
}
